import React, { useEffect, useState } from 'react';
import clsx from 'clsx';

interface PriceInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'value' | 'type'
  > {
  value?: number | string | null;
  onChange?: (value: number | undefined) => void;
  error?: string;
  inputClassName?: string;
  allowNegative?: boolean;
  maxDecimals?: number;
  decimalSeparator?: 'comma' | 'dot';
}

const formatPriceDisplay = (
  numericValue: number,
  maxDecimals: number,
  decimalSeparator: 'comma' | 'dot'
): string => {
  if (!isFinite(numericValue)) return '';

  // Round to the configured precision for stability, but do not force trailing zeros.
  const rounded = Number(numericValue.toFixed(maxDecimals));
  if (Number.isInteger(rounded)) {
    return String(Math.trunc(rounded));
  }

  const [integerPart, decimalPartRaw] = rounded.toString().split('.');

  if (!decimalPartRaw) {
    return String(Math.trunc(rounded));
  }

  return `${integerPart}${decimalSeparator === 'comma' ? ',' : '.'}${decimalPartRaw}`;
};

const PriceInput: React.FC<PriceInputProps> = ({
  value,
  onChange,
  error,
  inputClassName,
  allowNegative = false,
  maxDecimals = 2,
  decimalSeparator = 'comma',
  ...rest
}) => {
  const [displayValue, setDisplayValue] = useState<string>('');

  const errorId = rest.id ? `${rest.id}-error` : undefined;
  const describedByFromProps = rest['aria-describedby'];
  const combinedDescribedBy = error
    ? [describedByFromProps, errorId].filter(Boolean).join(' ')
    : describedByFromProps;

  useEffect(() => {
    if (value === null || value === undefined || value === '') {
      setDisplayValue('');
      return;
    }

    const numericValue =
      typeof value === 'number'
        ? value
        : parseFloat(
            String(value)
              .replace(/[^0-9.,-]/g, '')
              .replace(',', '.')
          );

    if (isNaN(numericValue)) {
      setDisplayValue('');
      return;
    }

    setDisplayValue(
      formatPriceDisplay(numericValue, maxDecimals, decimalSeparator)
    );
  }, [decimalSeparator, maxDecimals, value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    // Allow empty input
    if (raw === '') {
      setDisplayValue('');
      if (onChange) onChange(undefined);
      return;
    }

    // Keep digits and a single separator, normalize all separators to the configured symbol.
    let cleaned = raw.replace(
      allowNegative ? /[^0-9.,-]/g : /[^0-9.,]/g,
      ''
    );
    const separatorSymbol = decimalSeparator === 'comma' ? ',' : '.';
    const alternateSeparator = decimalSeparator === 'comma' ? '.' : ',';
    const separatorPattern =
      decimalSeparator === 'comma' ? /\./g : /,/g;
    cleaned = cleaned.replace(separatorPattern, separatorSymbol);

    const isNegative = allowNegative && raw.trim().startsWith('-');
    cleaned = cleaned.replace(/-/g, '');

    // If multiple commas, keep the first and drop the rest.
    const firstSeparatorIndex = cleaned.indexOf(separatorSymbol);
    if (firstSeparatorIndex !== -1) {
      const before = cleaned.slice(0, firstSeparatorIndex);
      const after = cleaned
        .slice(firstSeparatorIndex + 1)
        .replace(new RegExp(`\\${separatorSymbol}`, 'g'), '')
        .replace(new RegExp(`\\${alternateSeparator}`, 'g'), '');
      cleaned = before + separatorSymbol + after;
    }

    if (isNegative) {
      cleaned = `-${cleaned}`;
    }

    if (cleaned === '-') {
      setDisplayValue(cleaned);
      if (onChange) onChange(undefined);
      return;
    }

    const [intDisplay, decDisplayRaw = ''] = cleaned.split(separatorSymbol);

    if (maxDecimals === 0) {
      cleaned = intDisplay;
      setDisplayValue(cleaned);

      if (cleaned === '-' || cleaned === '') {
        if (onChange) onChange(undefined);
        return;
      }

      const parsedInteger = parseFloat(cleaned);
      if (onChange) {
        onChange(isNaN(parsedInteger) ? undefined : parsedInteger);
      }
      return;
    }

    // Limit displayed decimal digits to the configured precision, but allow a trailing separator.
    if (decDisplayRaw.length > maxDecimals) {
      const decDisplay = decDisplayRaw.slice(0, maxDecimals);
      cleaned = `${intDisplay}${separatorSymbol}${decDisplay}`;
    } else if (cleaned.includes(separatorSymbol)) {
      cleaned = `${intDisplay}${separatorSymbol}${decDisplayRaw}`;
    } else {
      cleaned = intDisplay;
    }

    if (cleaned === `-${separatorSymbol}`) {
      cleaned = '-';
    }

    // At this point `cleaned` is exactly what we want to display.
    setDisplayValue(cleaned);

    if (
      cleaned === '-' ||
      cleaned === `-0${separatorSymbol}` ||
      cleaned === separatorSymbol
    ) {
      if (onChange) onChange(undefined);
      return;
    }

    // Build numeric value for parent (dot-decimal).
    const [intPart = '', decPartRaw = ''] = cleaned.split(separatorSymbol);
    const decimalPart = decPartRaw.slice(0, maxDecimals);

    const numericString =
      decimalPart.length > 0
        ? `${intPart || '0'}.${decimalPart}`
        : intPart || '0';
    const parsed = parseFloat(numericString);

    if (onChange) {
      onChange(isNaN(parsed) ? undefined : parsed);
    }
  };

  const combinedInputClassName = clsx(
    'input input-bordered w-full h-12 rounded-lg border-2 transition-all duration-200',
    error && 'border-red-500',
    inputClassName
  );

  return (
    <>
      <input
        type="text"
        inputMode="decimal"
        value={displayValue}
        autoComplete="off"
        onChange={handleChange}
        className={combinedInputClassName}
        aria-invalid={!!error}
        aria-describedby={combinedDescribedBy}
        {...rest}
      />
      {error && (
        <p id={errorId} className="text-red-500 text-xs mt-2" role="alert">
          {error}
        </p>
      )}
    </>
  );
};

export default PriceInput;
